/* eslint-disable eslint-comments/disable-enable-pair */
// Added prevent optional chaining being removed from window.braintree?.client
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import { TFunction } from 'next-i18next'

import { CONFIG_BRAINTREE_APPLE_PAY_TOKENIZATION_KEY } from '@src/config/env'
import { COUNTRY_CURRENCIES } from '@src/config/localisation/locale-config.derived-constants'
import { AddressData, PartialAddress } from '@src/quince-site-common/src/services/addressService/addressType'
import { SHIPPING_METHODS } from '@src/quince-site-common/src/services/cartCheckoutService/checkout.service.types'
import {
  AvailableFulfillmentOption,
  CheckoutExpressUpdateDetail,
  DEFAULT_SHIPPING_METHOD,
  EnrichedCheckout,
  ShippingMethods,
} from '@src/quince-site-common/src/services/checkout-service-full/checkout-service-full.types'
import {
  EXPRESS_PAYMENT_SOURCE,
  ExpressPaymentSource,
} from '@src/quince-site-common/src/services/payment-service/payment-service.types'
import unifiedPaymentGatewayService from '@src/quince-site-common/src/services/unified-payment-gateway/unified-payment-gateway.service'
import { handleApplePayPaymentInitiatedTracking } from '@src/quince-site-common/src/trackers/helpers/handleApplePayTracking'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'
import {
  PDP_EXPRESS_ACTIONS,
  PDP_EXPRESS_APPLE_PAY,
  trackPdpExpressAction,
} from '@src/telemetry/pdp-express-rum.helper'
import {
  trackUnifiedPaymentAuthorised,
  trackUnifiedPaymentInitiated,
  UnifiedPaymentGateway,
} from '@src/telemetry/track-unified-checkout-events'
import { isDefined } from '@src/utils/functions/utils'
import { getLocalisationDataWithFallbacks } from '@src/utils/general.utils'
import { getNonce } from '@src/utils/nonce.utils'

import type { ApplePay, ApplePayLineItem, ApplePayPayload, ApplePaySession, ApplePayStatusCodes } from 'braintree-web'

interface BraintreeScriptGlobal {
  client: { create: (config: { authorization: string }) => Promise<unknown> }
  applePay: { create: (config: { client: unknown }) => Promise<ApplePay> }
}

declare global {
  interface Window {
    braintree?: BraintreeScriptGlobal
  }
}

const BRAINTREE_VERSION = '3.134.0'
const BRAINTREE_BASE_URL = `https://js.braintreegateway.com/web/${BRAINTREE_VERSION}/js`
const BRAINTREE_CLIENT_SCRIPT_ID = 'braintree-client-sdk'
const BRAINTREE_APPLE_PAY_SCRIPT_ID = 'braintree-apple-pay-sdk'

export const TRACKING_PAYMENT_GATEWAY_BRAINTREE: UnifiedPaymentGateway = 'BRAINTREE'

function loadScript(src: string, scriptId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = scriptId
    script.src = src
    script.async = true
    const nonce = getNonce()
    if (nonce) {
      script.setAttribute('nonce', nonce)
    }
    script.onload = () => {
      resolve()
    }
    script.onerror = () => {
      reject(new Error(`Failed to load script: ${src}`))
    }
    document.body.appendChild(script)
  })
}

/**
 * Loads Braintree Web SDK via script tags (v3 modular approach).
 * Loads client.min.js then apple-pay.min.js per Braintree Apple Pay v3 docs.
 * @see https://developer.paypal.com/braintree/docs/guides/apple-pay/client-side/javascript/v3/
 * Resolves with the global braintree object when both scripts are ready.
 * Reuses existing scripts if already loaded.
 */
export const loadBraintreeScript = (): Promise<BraintreeScriptGlobal> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Braintree script can only be loaded in the browser'))
  }
  if (window.braintree?.client && window.braintree?.applePay) {
    return Promise.resolve(window.braintree)
  }
  const clientLoaded = document.getElementById(BRAINTREE_CLIENT_SCRIPT_ID)
  const applePayLoaded = document.getElementById(BRAINTREE_APPLE_PAY_SCRIPT_ID)
  if (clientLoaded && applePayLoaded) {
    return new Promise<BraintreeScriptGlobal>((resolve, reject) => {
      const timeoutMs = 15000
      const start = Date.now()
      const check = () => {
        if (window.braintree?.client && window.braintree?.applePay) {
          resolve(window.braintree)
        } else if (Date.now() - start >= timeoutMs) {
          reject(new Error('Timeout waiting for Braintree scripts'))
        } else {
          setTimeout(check, 50)
        }
      }
      check()
    })
  }
  const loadClient = clientLoaded
    ? Promise.resolve()
    : loadScript(`${BRAINTREE_BASE_URL}/client.min.js`, BRAINTREE_CLIENT_SCRIPT_ID)
  const loadApplePay = () =>
    applePayLoaded
      ? Promise.resolve()
      : loadScript(`${BRAINTREE_BASE_URL}/apple-pay.min.js`, BRAINTREE_APPLE_PAY_SCRIPT_ID)

  function waitForBraintree(timeoutMs = 5000): Promise<BraintreeScriptGlobal> {
    return new Promise((resolve, reject) => {
      const start = Date.now()
      const check = () => {
        if (window.braintree?.client && window.braintree?.applePay) {
          resolve(window.braintree)
        } else if (Date.now() - start >= timeoutMs) {
          reject(new Error('Timeout: window.braintree.client or applePay did not appear after loading scripts'))
        } else {
          setTimeout(check, 30)
        }
      }
      check()
    })
  }

  return loadClient.then(loadApplePay).then(() => waitForBraintree())
}

export const initializeApplePay = async (
  onError?: (error?: unknown) => void,
  braintreeLoader: () => Promise<BraintreeScriptGlobal> = loadBraintreeScript,
) => {
  if (
    !window.ApplePaySession ||
    !window.ApplePaySession.supportsVersion?.(3) ||
    !window.ApplePaySession.canMakePayments?.()
  ) {
    return {
      applePayInstance: null,
      isSupported: false,
    }
  }
  try {
    const braintree = await braintreeLoader()

    const client = await braintree.client.create({
      authorization: CONFIG_BRAINTREE_APPLE_PAY_TOKENIZATION_KEY,
    })
    const applePayInstance = await braintree.applePay.create({ client })
    return {
      applePayInstance,
      isSupported: true,
    }
  } catch (err) {
    console.error('Error creating Braintree ApplePay instance:', err)
    onError?.(new Error('Apple Pay initialization failed'))
    return {
      applePayInstance: null,
      isSupported: false,
    }
  }
}

const AMOUNT_BREAKDOWN_LABELS = {
  SUBTOTAL: 'Subtotal',
  SHIPPING: 'Shipping',
  STORE_CREDIT: 'Store Credit',
  TAXES: 'Taxes',
  GIFT_CARD: 'Gift Card',
  RETURN_ESTIMATE: 'Return Estimate',
}

export const makeCheckoutExpressUpdateDetail: (
  checkout: EnrichedCheckout | null | undefined,
  sessionId?: number,
  defaultSubtotal?: number,
) => CheckoutExpressUpdateDetail = (checkout, sessionId, defaultSubtotal) => {
  const giftCardTransactions =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    checkout?.paymentSummary?.paymentTransactionDetails?.filter(transaction => transaction.mode === 'GIFT_CARD') || []
  const storeCreditAmountUsed = checkout?.paymentSummary?.storeCreditAmountUsed

  return {
    amountBreakdown: [
      isDefined(checkout?.costSummary?.subTotalAmountToDisplay)
        ? {
            label: AMOUNT_BREAKDOWN_LABELS.SUBTOTAL,
            amount: checkout.costSummary.subTotalAmountToDisplay,
          }
        : {
            label: AMOUNT_BREAKDOWN_LABELS.SUBTOTAL,
            amount: defaultSubtotal,
          },
      isDefined(checkout?.costSummary?.totalShippingAmount)
        ? {
            label: AMOUNT_BREAKDOWN_LABELS.SHIPPING,
            amount: checkout.costSummary.totalShippingAmount,
          }
        : undefined,
      isDefined(storeCreditAmountUsed)
        ? {
            label: AMOUNT_BREAKDOWN_LABELS.STORE_CREDIT,
            amount: -storeCreditAmountUsed,
          }
        : undefined,
      isDefined(checkout?.costSummary?.totalTax)
        ? {
            label: AMOUNT_BREAKDOWN_LABELS.TAXES,
            amount: checkout.costSummary.totalTax,
          }
        : undefined,
      ...giftCardTransactions.map(transaction => ({
        label: AMOUNT_BREAKDOWN_LABELS.GIFT_CARD,
        amount: -transaction.amount,
      })),
      isDefined(checkout?.costSummary?.totalDiscountToDisplay)
        ? {
            label: checkout.cart?.discountCode,
            amount: checkout.costSummary.totalDiscountToDisplay && -checkout.costSummary.totalDiscountToDisplay,
          }
        : undefined,
      isDefined(checkout?.adjustmentCreditInfo?.amount)
        ? {
            label: AMOUNT_BREAKDOWN_LABELS.RETURN_ESTIMATE,
            amount: checkout.adjustmentCreditInfo.amount && -checkout.adjustmentCreditInfo.amount,
          }
        : undefined,
    ].filter(item => item?.amount != undefined && item.label) as {
      label: string
      amount: number
    }[],
    amountToCollect: checkout?.paymentSummary?.balanceAmount || defaultSubtotal || 0,
    availableFulfillmentOptions: checkout?.availableFulfillmentOptions,
    checkoutId: checkout?.id,
    sessionId,
    checkout: checkout,
    fixedShippingAddress: checkout?.shippingAddress,
    fixedEmailAddress: checkout?.customerDetails?.email,
  }
}

const CONTACT_FIELDS = {
  NAME: 'name',
  EMAIL: 'email',
  PHONE: 'phone',
  POSTAL_ADDRESS: 'postalAddress',
}
const FINAL_DEFAULT_TYPE = 'final'
const SHIPPING_TYPE = 'shipping'

export const createApplePaySession = (
  applePayInstance: ApplePay,
  checkoutExpressUpdateDetail: CheckoutExpressUpdateDetail,
  isPhoneNumberRequired: boolean,
  t: TFunction,
  skipEmail = false,
) => {
  try {
    const requiredBillingContactFields = [CONTACT_FIELDS.POSTAL_ADDRESS, CONTACT_FIELDS.NAME, CONTACT_FIELDS.PHONE]
    const requiredShippingContactFields = isPhoneNumberRequired
      ? [CONTACT_FIELDS.POSTAL_ADDRESS, CONTACT_FIELDS.NAME, CONTACT_FIELDS.PHONE]
      : [CONTACT_FIELDS.POSTAL_ADDRESS, CONTACT_FIELDS.NAME]
    if (!skipEmail) {
      requiredBillingContactFields.push(CONTACT_FIELDS.EMAIL)
      requiredShippingContactFields.push(CONTACT_FIELDS.EMAIL)
    }
    const { currency, country } = getLocalisationDataWithFallbacks(undefined, { useLocaleCookieFallback: true })
    const countryCode = country.toUpperCase()
    const currencyCode = (
      COUNTRY_CURRENCIES[country.toLowerCase() as keyof typeof COUNTRY_CURRENCIES] ?? currency
    ).toUpperCase()

    const paymentRequest = applePayInstance.createPaymentRequest({
      total: {
        label: 'Quince',
        amount: checkoutExpressUpdateDetail.amountToCollect.toFixed(2),
      },
      currencyCode,
      countryCode,
      lineItems: makeBraintreeLineItems(checkoutExpressUpdateDetail, FINAL_DEFAULT_TYPE, true),
      requiredBillingContactFields,
      requiredShippingContactFields,
      shippingMethods: makeApplePayBraintreeShippingMethods(checkoutExpressUpdateDetail, t),
      shippingType: SHIPPING_TYPE,
    })

    if (!window.ApplePaySession) {
      throw new Error('Apple Pay is not supported')
    }

    // MUST be created immediately in the click handler - no await before this!
    return new window.ApplePaySession(3, paymentRequest)
  } catch (error) {
    console.error('Error creating Apple Pay session:', error)
    return null
  }
}

const labelToType = (label: string) => {
  if (label === 'Subtotal') return 'SUBTOTAL'
  if (label === 'Shipping') return 'SHIPPING_OPTION'
  if (label === 'Taxes') return 'TAX'
  return 'LINE_ITEM'
}

export const makeBraintreeLineItems = (
  checkoutExpressUpdateDetail: CheckoutExpressUpdateDetail,
  defaultType?: string,
  isAmount?: boolean,
) => {
  return checkoutExpressUpdateDetail.amountBreakdown.map(({ label, amount }) => {
    const baseAmountBreakdown = {
      label: label,
      type: defaultType || labelToType(label),
    } as ApplePayLineItem
    return isAmount
      ? { ...baseAmountBreakdown, amount: amount.toFixed(2) }
      : { ...baseAmountBreakdown, price: amount.toFixed(2) }
  })
}

export const EDD_TYPE = {
  BUSINESS: 'BUSINESS',
  CALENDAR: 'CALENDAR',
}

const DELIVERY_DATE_TIME_FORMAT = 'T00:00:00'
const formatDeliveryDate = (deliveryDate: string, t: TFunction) => {
  let formattedDate = deliveryDate
  const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

  if (dateRegex.test(deliveryDate)) {
    formattedDate += DELIVERY_DATE_TIME_FORMAT
  }

  const date = new Date(formattedDate)
  const day = date.getDay()
  const dayOfMonth = date.getDate()
  const month = date.getMonth()

  return {
    day: getDayNames(t)[day],
    date: `${getMonthNames(t)[month]} ${dayOfMonth}`,
  }
}

export const getDayNames = (t: TFunction) => [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')]

export const getMonthNames = (t: TFunction) => [
  t('jan'),
  t('feb'),
  t('mar'),
  t('apr'),
  t('may'),
  t('jun'),
  t('jul'),
  t('aug'),
  t('sep'),
  t('oct'),
  t('nov'),
  t('dec'),
]

export const getDeliveryTimeText = (fulfillmentOption: AvailableFulfillmentOption, t: TFunction) => {
  const { maxCalDays, minCalDays, shippingMethod, eddType, maxDeliveryDate, minDaysToDelivery, maxDaysToDelivery } =
    fulfillmentOption

  const dayType = eddType === EDD_TYPE.CALENDAR ? t('days') : t('BUSINESS_DAYS')

  // Handle same day delivery (0 calendar days)
  if (maxCalDays === 0 && minCalDays === 0) {
    const { date } = formatDeliveryDate(maxDeliveryDate, t)
    // Only use "Get it by" format for STANDARD_PREMIUM shipping method
    if (shippingMethod.name === SHIPPING_METHODS.STANDARD_PREMIUM) {
      return t('DELIVERY_TIME_TEXT_STANDARD_PREMIUM', {
        day: t('DELIVERY_TODAY'),
        date: date,
      })
    }
    // For other shipping methods, use simple "Today, date" format
    return t('DELIVERY_DAY_DATE', {
      day: t('DELIVERY_TODAY'),
      date: date,
    })
  }

  // Handle next day delivery (1 calendar day)
  if (maxCalDays === 1 && minCalDays <= 1) {
    const { date } = formatDeliveryDate(maxDeliveryDate, t)
    // Only use "Get it by" format for STANDARD_PREMIUM shipping method
    if (shippingMethod.name === SHIPPING_METHODS.STANDARD_PREMIUM) {
      return t('DELIVERY_TIME_TEXT_STANDARD_PREMIUM', {
        day: t('DELIVERY_TOMORROW'),
        date: date,
      })
    }
    // For other shipping methods, use simple "Tomorrow, date" format
    return t('DELIVERY_DAY_DATE', {
      day: t('DELIVERY_TOMORROW'),
      date: date,
    })
  }

  // Handle 2-day delivery
  if (maxCalDays === 2 && minCalDays <= 2) {
    return t('DELIVERY_TIME_TWO_DAY')
  }

  // Handle STANDARD_PREMIUM with special formatting (existing logic)
  if (shippingMethod.name === SHIPPING_METHODS.STANDARD_PREMIUM) {
    const { day, date } = formatDeliveryDate(maxDeliveryDate, t)
    return t('DELIVERY_TIME_TEXT_STANDARD_PREMIUM', {
      day: day,
      date: date,
    })
  }

  // Handle mixed scenarios (different min/max calendar days)
  if (minCalDays !== maxCalDays) {
    // Same day mixed with longer delivery
    if (minCalDays === 0) {
      return t('DELIVERY_TIME_MIXED_SAME_DAY', {
        maxDays: maxDaysToDelivery,
        dayType: dayType,
      })
    }

    // 2-day mixed with longer delivery
    if (minCalDays === 2) {
      return t('DELIVERY_TIME_MIXED_TWO_DAY', {
        maxDays: maxDaysToDelivery,
        dayType: dayType,
      })
    }
  }

  // Default range-based delivery (existing logic)
  return t('DELIVERY_TIME_TEXT', {
    minDays: minDaysToDelivery,
    maxDays: maxDaysToDelivery,
    dayType: dayType,
  })
}

export const makeApplePayBraintreeShippingMethods = (
  checkoutExpressUpdateDetail: CheckoutExpressUpdateDetail,
  t: TFunction,
) => {
  if (!checkoutExpressUpdateDetail.availableFulfillmentOptions) {
    return [
      {
        label: t('STANDARD'),
        amount: '0.00',
        identifier: 'LB_STANDARD',
        detail: '',
      },
    ]
  }

  return checkoutExpressUpdateDetail.availableFulfillmentOptions.map(availableFulfillmentOption => {
    const detail = getDeliveryTimeText(availableFulfillmentOption, t)

    return {
      label: availableFulfillmentOption.shippingMethod.displayName,
      amount: availableFulfillmentOption.shippingMethod.amount?.toString() || '0.00',
      detail,
      identifier: availableFulfillmentOption.shippingMethod.name,
    }
  })
}

export interface BraintreeApplePayMerchantValidationEvent {
  validationURL: string
}

export interface OnShippingContactSelectedEvent {
  shippingContact: {
    locality: string
    administrativeArea: string
    postalCode: string
    countryCode: string
  }
}

export interface OnShippingMethodSelectedEvent {
  shippingMethod: {
    identifier: string
  }
}

export const getPartialAddress = async (
  event: OnShippingContactSelectedEvent,
  updateShippingAddress: (address: PartialAddress) => Promise<CheckoutExpressUpdateDetail>,
) => {
  const partialAddress = await updateShippingAddress({
    city: event.shippingContact.locality,
    provinceCode: event.shippingContact.administrativeArea,
    zip: event.shippingContact.postalCode,
    countryCode: event.shippingContact.countryCode,
  })
  const newTotal = {
    label: 'Quince',
    amount: partialAddress.amountToCollect.toFixed(2),
  }
  return { partialAddress, newTotal }
}

interface ApplePayShippingContactError {
  code: 'shippingContactInvalid'
  contactField: 'postalAddress'
  message: string
}

interface ApplePayFallbackDetail {
  shippingMethods: unknown
  newTotal: unknown
  lineItems: unknown
}

// Only the v3 dictionary form (errors array) renders a message on the sheet; braintree types cover
// only the legacy signature, hence the casts. Returns false — so the caller can close the sheet
// rather than leave it open error-less — when ApplePayError is missing or the runtime rejects the call.
export const completeShippingContactSelectionWithError = (
  session: ApplePaySession,
  message: string,
  fallback: ApplePayFallbackDetail,
): boolean => {
  try {
    const ApplePayErrorCtor = (
      window as unknown as {
        ApplePayError?: new (code: string, contactField: string, message: string) => ApplePayShippingContactError
      }
    ).ApplePayError

    if (!ApplePayErrorCtor) return false

    const sessionWithErrors = session as unknown as {
      completeShippingContactSelection: (update: {
        errors: ApplePayShippingContactError[]
        newShippingMethods: unknown
        newTotal: unknown
        newLineItems: unknown
      }) => void
    }
    sessionWithErrors.completeShippingContactSelection({
      errors: [new ApplePayErrorCtor('shippingContactInvalid', 'postalAddress', message)],
      newShippingMethods: fallback.shippingMethods,
      newTotal: fallback.newTotal,
      newLineItems: fallback.lineItems,
    })
    return true
  } catch (err) {
    console.error('Failed to show Apple Pay shipping contact error:', err)
    return false
  }
}

interface ShippingContact {
  emailAddress: string
  addressLines?: string[]
  locality?: string
  postalCode?: string
  administrativeArea?: string
  countryCode?: string
  phoneNumber?: string
  givenName?: string
  familyName?: string
}

export type UpdateExpressCompleteDetails = (data: {
  billingAddress: AddressData
  shippingAddress?: AddressData
  selectedShippingOption: ShippingMethods
  selectedPaymentSource: ExpressPaymentSource
  email?: string
}) => Promise<CheckoutExpressUpdateDetail>

export interface OnPaymentAuthorizedEvent {
  payment: {
    token: string
    shippingContact: ShippingContact
    billingContact: ShippingContact
  }
}

export const createAddressFromContact = (contact?: ShippingContact) => ({
  addressLine1: contact?.addressLines?.[0] || '',
  addressLine2: contact?.addressLines?.[1],
  city: contact?.locality || '',
  zip: contact?.postalCode || '',
  provinceCode: contact?.administrativeArea || '',
  countryCode: contact?.countryCode || '',
  phoneNumber: contact?.phoneNumber,
  firstName: contact?.givenName || '',
  lastName: contact?.familyName || '',
})

interface DataCollectorInstance {
  deviceData?: string
}

export const createAuthorizationPayload = (
  payload?: ApplePayPayload,
  event?: OnPaymentAuthorizedEvent,
  dataCollectorInstance?: DataCollectorInstance,
  customerEmail = '',
) => ({
  paymentToken: payload?.nonce,
  gatewayParams: {
    deviceData: dataCollectorInstance?.deviceData || '',
  },
  customer: {
    email: customerEmail,
  },
  shippingAddress: createAddressFromContact(event?.payment.shippingContact),
  billingAddress: createAddressFromContact(event?.payment.billingContact),
})

export const updateExpressDetails = async (
  event: OnPaymentAuthorizedEvent,
  checkoutExpressUpdateDetail: CheckoutExpressUpdateDetail | null,
  updateExpressCompleteDetails: UpdateExpressCompleteDetails,
  customerEmail: string,
) => {
  const shippingAddress = createAddressFromContact(event.payment.shippingContact)
  const billingAddress = createAddressFromContact(event.payment.billingContact)

  const completePayload = {
    billingAddress: billingAddress,
    shippingAddress: shippingAddress,
    selectedShippingOption: DEFAULT_SHIPPING_METHOD,
    selectedPaymentSource: EXPRESS_PAYMENT_SOURCE.APPLE_PAY_EXPRESS,
    email: customerEmail,
  }

  return await updateExpressCompleteDetails(completePayload)
}

export const handlePaymentAuthorization = async (
  event: OnPaymentAuthorizedEvent,
  session: ApplePaySession,
  applePayInstance: ApplePay,
  checkoutExpressUpdateDetail: CheckoutExpressUpdateDetail | null,
  updateExpressCompleteDetails: UpdateExpressCompleteDetails,
  capture: () => Promise<void>,
  skipEmail = false,
) => {
  return new Promise<void>((resolve, reject) => {
    const applePayEventRequest = {
      checkoutId: checkoutExpressUpdateDetail?.checkoutId,
      sessionId: checkoutExpressUpdateDetail?.sessionId,
      amountToCollect: checkoutExpressUpdateDetail?.amountToCollect,
    }
    handleApplePayPaymentInitiatedTracking(applePayEventRequest)
    applePayInstance.tokenize(
      {
        token: event.payment.token,
      },
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async function (tokenizeErr: unknown, payload?: ApplePayPayload) {
        if (tokenizeErr) {
          console.error('Error tokenizing Apple Pay:', tokenizeErr)
          session.completePayment(window.ApplePaySession?.STATUS_FAILURE as ApplePayStatusCodes)
          reject(tokenizeErr as Error)
          return
        }

        const walletEmail = event.payment.shippingContact.emailAddress || ''
        const checkoutEmail = checkoutExpressUpdateDetail?.checkout?.email || ''
        const fixedEmail = checkoutExpressUpdateDetail?.fixedEmailAddress || ''
        const customerEmail = skipEmail ? fixedEmail : walletEmail || checkoutEmail

        if (skipEmail && !customerEmail) {
          session.completePayment(window.ApplePaySession?.STATUS_FAILURE as ApplePayStatusCodes)
          reject(new Error('Fixed email missing'))
          return
        }

        if (!skipEmail && !validateEmail(customerEmail)) {
          session.completePayment(window.ApplePaySession?.STATUS_FAILURE as ApplePayStatusCodes)
          reject(new Error('Invalid email address'))
          return
        }

        try {
          const authorizePayload = createAuthorizationPayload(payload, event, undefined, customerEmail)

          const updatedCheckoutExpressUpdateDetail = await updateExpressDetails(
            event,
            checkoutExpressUpdateDetail,
            updateExpressCompleteDetails,
            customerEmail,
          )
          trackUnifiedPaymentInitiated({ paymentGateway: TRACKING_PAYMENT_GATEWAY_BRAINTREE })
          trackPdpExpressAction(
            PDP_EXPRESS_ACTIONS.PAYMENT_INITIATED,
            PDP_EXPRESS_APPLE_PAY,
            TRACKING_PAYMENT_GATEWAY_BRAINTREE,
          )
          await unifiedPaymentGatewayService.authorizePayment(
            authorizePayload,
            updatedCheckoutExpressUpdateDetail.sessionId,
          )
          trackUnifiedPaymentAuthorised()
          trackPdpExpressAction(
            PDP_EXPRESS_ACTIONS.PAYMENT_AUTHORISED,
            PDP_EXPRESS_APPLE_PAY,
            TRACKING_PAYMENT_GATEWAY_BRAINTREE,
          )

          await capture()
          session.completePayment(window.ApplePaySession?.STATUS_SUCCESS as ApplePayStatusCodes)
          resolve()
        } catch (error: unknown) {
          console.error('payment flow error for Apple Pay', error)
          session.completePayment(window.ApplePaySession?.STATUS_FAILURE as ApplePayStatusCodes)
          reject(error as Error)
        }
      },
    )
  })
}
